// The Resources landing page — five doors into one table.
//
// Parity pass 2 batch 1. Before this, Resources was a single list of three
// kinds and a filter dropdown. Six kinds is too many for that: a person
// scanning for "where do I add the truck" should not have to know that the
// truck is a row in the same table as Jack Miller.
//
// The counts are the point of the page. An empty group says so on its own card
// rather than leaving someone to click through and find out, and the Assets
// card carries the two numbers a fleet owner actually opens this page to see:
// how many regos have lapsed, and how many services are due.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { scheduling, type Resource } from '../schedulingApi';
import { fleet, type AssetDue } from '../fleetApi';
import { ApiError } from '../api';
import { RESOURCE_GROUPS } from '../lib/resourceGroups';
import { EmptyState, PageHeader, StatusBadge } from '../components/ui';

export function ResourcesHomePage() {
  const [resources, setResources] = useState<Resource[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [dueAssets, setDueAssets] = useState<AssetDue[] | null>(null);

  useEffect(() => {
    // One request for everything, then counted in the browser. Five count
    // queries would be five round trips for numbers that are, at this
    // product's scale, a few dozen rows.
    void scheduling.listResources().then(
      (result) => setResources(result.resources),
      (err) => setError(err instanceof ApiError ? err.message : 'Could not load your resources.')
    );
    // The service-due answer comes from the server, which is the only place
    // that knows both halves of the rule — see the count below. A failure here
    // is not worth a banner: the page's other numbers are still right, and the
    // badge simply does not appear.
    void fleet.serviceDue().then(
      (result) => setDueAssets(result.assets),
      () => setDueAssets(null)
    );
  }, []);

  const countIn = (kinds: string[]) =>
    (resources ?? []).filter((r) => kinds.includes(r.kind)).length;

  const assets = (resources ?? []).filter((r) => r.kind === 'plant' || r.kind === 'vehicle');
  const regoExpired = assets.filter((r) => r.registrationExpiryStatus === 'expired').length;
  const regoSoon = assets.filter((r) => r.registrationExpiryStatus === 'soon').length;
  // BOTH HALVES OF THE RULE, from the endpoint that knows them.
  //
  // This counted `nextServiceDate <= today` and nothing else — the batch-1
  // shape, with a comment promising it would grow when meters arrived in batch
  // 2. Meters arrived; this did not. So an excavator forty hours past its
  // service interval was simply not counted here, while Home's reminders —
  // which do read the meter — said it was due. Two screens in the same product
  // disagreeing about the same machine, and the one on the Resources page was
  // the one quietly saying everything was fine.
  //
  // `fleet.serviceDue` was already written and already returned
  // `serviceDueStatus`; it just had no caller.
  const serviceDue =
    dueAssets === null
      ? null
      : dueAssets.filter((a) => a.serviceDueStatus === 'overdue').length;

  return (
    <>
      <PageHeader
        title="Resources"
        subtitle="Everything you schedule, and everything you look after."
        actions={
          // The yard-walk screen had no way in from the UI until the guides
          // work (2026-08-26) — the route existed, nothing linked it.
          <Link to="/app/resources/readings" className="btn-secondary btn-sm">
            Enter readings
          </Link>
        }
      />

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {resources !== null && resources.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          body="Resources are the things you schedule: your crew, your plant, your vehicles. Start with whichever list you have to hand; you can import a spreadsheet instead of typing them in."
          action={
            <Link to="/app/resources/people" className="btn-primary btn-sm">
              Add your crew
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RESOURCE_GROUPS.map((group) => {
            const count = countIn(group.kinds);
            const isAssets = group.key === 'assets';

            return (
              <Link
                key={group.key}
                to={`/app/resources/${group.path}`}
                // min-h keeps the five cards a uniform height whatever the
                // blurb wraps to, and comfortably past the 44px touch floor.
                className="flex min-h-[7.5rem] flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-brand-400 hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-base font-semibold text-gray-900">{group.label}</h2>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold tabular-nums text-gray-700">
                    {resources === null ? '—' : count}
                  </span>
                </div>

                <p className="mt-1 flex-1 text-sm text-gray-600">{group.blurb}</p>

                {isAssets && (regoExpired > 0 || regoSoon > 0 || (serviceDue ?? 0) > 0) && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {regoExpired > 0 && (
                      <StatusBadge kind="expiry" status="expired" label={`${regoExpired} rego expired`} />
                    )}
                    {regoSoon > 0 && (
                      <StatusBadge kind="expiry" status="soon" label={`${regoSoon} rego due soon`} />
                    )}
                    {(serviceDue ?? 0) > 0 && (
                      <StatusBadge kind="expiry" status="soon" label={`${serviceDue} service due`} />
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
